import { Controller } from "@hotwired/stimulus"
import { createGrid, ModuleRegistry } from "ag-grid-community"
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise"

// Register all Community and Enterprise features
ModuleRegistry.registerModules([AllEnterpriseModule])

// Suppress license warning in development (remove in production with valid license)
LicenseManager.setLicenseKey("")

// Generic AG Grid controller with configurable options
// Connects to data-controller="aggrid"
export default class extends Controller {
  static targets = ["grid", "searchInput", "tab", "count"]
  static values = {
    // Data source
    dataUrl: String,
    rowData: Array,

    // Column configuration
    columnDefs: Array,

    // Grid options
    theme: { type: String, default: "ag-theme-quartz" },
    height: { type: String, default: "500px" },
    rowHeight: { type: Number, default: 42 },
    dynamicHeight: { type: Boolean, default: false },
    heightOffset: { type: Number, default: 100 },
    pagination: { type: Boolean, default: false },
    paginationPageSize: { type: Number, default: 25 },
    rowSelection: { type: String, default: "single" },
    animateRows: { type: Boolean, default: true },

    // Filtering
    filterField: String, // Field to use for tab filtering (e.g., "type", "enabled")
    filterValues: Array, // Array of {value: "x", label: "Label"} for tabs

    // URLs for CRUD operations
    baseUrl: String,

    // Cell renderer templates - JSON object mapping field names to renderer types
    // e.g., {"title": "link", "enabled": "checkbox", "type": "badge"}
    cellRenderers: Object,

    // Badge colors - JSON object mapping values to color classes
    // e.g., {"Policy": "blue", "Qna": "purple"}
    badgeColors: Object,

    // Link fields - which fields should link to show/edit
    linkField: String,

    // Checkbox toggle URL pattern for inline updates
    toggleUrl: String,

    // Server-side search configuration
    serverSideSearch: { type: Boolean, default: false },
    searchDebounce: { type: Number, default: 300 }
  }

  connect() {
    this.currentFilter = "all"
    this.totalRowCount = 0
    this.initializeGrid()
    this.loadData()

    if (this.dynamicHeightValue) {
      this.resizeHandler = this.handleResize.bind(this)
      window.addEventListener("resize", this.resizeHandler)
    }
  }

  disconnect() {
    if (this.gridApi) {
      this.gridApi.destroy()
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler)
    }
  }

  initializeGrid() {
    // Apply theme class to grid container
    this.gridTarget.classList.add(this.themeValue)

    // Set height
    if (this.dynamicHeightValue) {
      this.gridTarget.style.height = this.calculateDynamicHeight()
    } else {
      this.gridTarget.style.height = this.heightValue
    }

    // Build rowSelection config based on value
    const rowSelectionConfig = this.rowSelectionValue === "multiple"
      ? { mode: "multiRow", enableClickSelection: true }
      : { mode: "singleRow", enableClickSelection: true }

    const gridOptions = {
      theme: "legacy",
      rowHeight: this.rowHeightValue,
      columnDefs: this.processColumnDefs(),
      rowData: this.hasRowDataValue ? this.rowDataValue : [],
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        flex: 1,
        minWidth: 100
      },
      animateRows: this.animateRowsValue,
      rowSelection: rowSelectionConfig,
      pagination: this.paginationValue,
      paginationPageSize: this.paginationPageSizeValue,
      domLayout: "normal",

      // External filtering for tabs
      isExternalFilterPresent: () => this.currentFilter !== "all",
      doesExternalFilterPass: (node) => this.doesExternalFilterPass(node),

      // Event handlers
      onGridReady: this.onGridReady.bind(this),
      onRowDoubleClicked: this.onRowDoubleClicked.bind(this),
      onRowDataUpdated: this.onRowDataUpdated.bind(this),

      getRowId: (params) => String(params.data.id),

      // Quick filter
      cacheQuickFilter: true,
      quickFilterText: ""
    }

    this.gridApi = createGrid(this.gridTarget, gridOptions)
  }

  processColumnDefs() {
    if (!this.hasColumnDefsValue) return []

    const columnDefs = JSON.parse(JSON.stringify(this.columnDefsValue))
    const renderers = this.hasCellRenderersValue ? this.cellRenderersValue : {}
    const badgeColors = this.hasBadgeColorsValue ? this.badgeColorsValue : {}
    const baseUrl = this.hasBaseUrlValue ? this.baseUrlValue : ""
    const linkField = this.hasLinkFieldValue ? this.linkFieldValue : null

    columnDefs.forEach(colDef => {
      const field = colDef.field
      const rendererType = renderers[field]

      if (rendererType) {
        colDef.cellRenderer = this.getCellRenderer(rendererType, field, baseUrl, badgeColors)
      }

      // If this is the link field, wrap content in a link
      if (linkField && field === linkField && !rendererType) {
        colDef.cellRenderer = (params) => {
          const url = params.data.show_url || `${baseUrl}/${params.data.id}`
          return `<a href="${url}" data-turbo-frame="_top" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline">${params.value || ""}</a>`
        }
      }
    })

    return columnDefs
  }

  getCellRenderer(type, field, baseUrl, badgeColors) {
    switch (type) {
      case "link":
        return (params) => {
          const url = params.data.show_url || `${baseUrl}/${params.data.id}`
          return `<a href="${url}" data-turbo-frame="_top" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline">${params.value || ""}</a>`
        }

      case "editLink":
        return (params) => {
          const url = params.data.edit_url || `${baseUrl}/${params.data.id}/edit`
          return `<a href="${url}" data-turbo-frame="_top" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline">${params.value || ""}</a>`
        }

      case "badge":
        return (params) => {
          const value = params.value
          const colorKey = params.data[field.replace("_label", "")] || value
          const color = badgeColors[colorKey] || "gray"
          const colorClasses = this.getBadgeColorClasses(color)
          return `<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses}">${value}</span>`
        }

      case "checkbox":
        return (params) => {
          const checked = params.value ? "checked" : ""
          return `<input type="checkbox" ${checked} data-id="${params.data.id}" data-action="change->aggrid#toggleCheckbox" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">`
        }

      case "tags":
        return (params) => {
          const tags = params.value
          if (!tags || tags.length === 0) return `<div class="flex h-full items-center"><span class="text-gray-400">-</span></div>`

          const displayed = tags.slice(0, 3)
          const extra = tags.length > 3 ? `<span class="text-xs text-gray-500 dark:text-gray-400">+${tags.length - 3}</span>` : ""
          const badges = displayed.map(t =>
            `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 dark:bg-neutral-700 dark:text-gray-300">${t}</span>`
          ).join("")
          return `<div class="flex flex-wrap gap-1 items-center h-full">${badges}${extra}</div>`
        }

      case "currency":
        return (params) => {
          const value = params.value
          if (value == null) return "-"
          const amount = typeof value === "number" ? value / 100 : parseFloat(value)
          return `<span class="text-right">$${amount.toFixed(2)}</span>`
        }

      case "actions":
        return (params) => {
          const showUrl = params.data.show_url || `${baseUrl}/${params.data.id}`
          const editUrl = params.data.edit_url || `${baseUrl}/${params.data.id}/edit`
          return `
            <div class="flex items-center gap-2">
              <a href="${editUrl}" data-turbo-frame="_top" class="text-blue-600 hover:text-blue-800 dark:text-blue-500 dark:hover:text-blue-400 text-sm">Edit</a>
              <a href="${showUrl}" data-turbo-frame="_top" class="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 text-sm">View</a>
            </div>
          `
        }

      case "titleWithIcon":
        return (params) => {
          const iconField = params.colDef.iconField || "type"
          const iconType = params.data[iconField]
          // Default icon mapping based on common types
          const defaultIcons = {
            "Policy": { icon: "document", color: "blue" },
            "Qna": { icon: "question", color: "purple" },
            "box": { icon: "box", color: "gray" },
            "satchel": { icon: "box", color: "blue" }
          }
          const icons = params.colDef.icons || defaultIcons
          const iconConfig = icons[iconType] || { icon: "document", color: "gray" }
          const url = params.data.show_url || `${baseUrl}/${params.data.id}`
          const subtitle = params.data.body || params.data.subtitle || null

          const bgColorClass = this.getIconBgClass(iconConfig.color)
          const iconHtml = this.getIcon(iconConfig.icon, iconConfig.color)

          return `
            <div class="flex items-center gap-3 py-1">
              <div class="flex-shrink-0 h-8 w-8 rounded-lg flex items-center justify-center ${bgColorClass}">
                ${iconHtml}
              </div>
              <div class="min-w-0 flex-1">
                <a href="${url}" data-turbo-frame="_top" class="text-sm font-medium text-gray-900 dark:text-neutral-200 hover:text-blue-600 dark:hover:text-blue-400 block truncate">${params.value}</a>
                ${subtitle ? `<div class="text-xs text-gray-500 dark:text-neutral-400 truncate max-w-[200px]">${subtitle}</div>` : ""}
              </div>
            </div>
          `
        }

      case "productWithImage":
        return (params) => {
          const url = params.data.show_url || `${baseUrl}/${params.data.id}`
          const imageUrl = params.data.image_url
          const handle = params.data.handle

          const imageHtml = imageUrl
            ? `<img class="h-10 w-10 rounded-lg object-cover" src="${imageUrl}" alt="${params.value}">`
            : `<div class="h-10 w-10 bg-gray-200 dark:bg-neutral-600 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-gray-400 dark:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>`

          return `
            <div class="flex items-center h-full">
              <a href="${url}" data-turbo-frame="_top" class="flex-shrink-0">${imageHtml}</a>
              <div class="ml-4 min-w-0">
                <a href="${url}" data-turbo-frame="_top" class="text-sm font-medium text-gray-900 dark:text-neutral-200 hover:text-blue-600 dark:hover:text-blue-400 block truncate">${params.value}</a>
                ${handle ? `<div class="text-xs text-gray-500 dark:text-neutral-400 mt-0.5"><code>${handle}</code></div>` : ""}
              </div>
            </div>
          `
        }

      case "priceWithSale":
        return (params) => {
          const price = params.data.price
          const compareAtPrice = params.data.compare_at_price
          const onSale = params.data.on_sale

          if (!price) return `<div class="flex items-center h-full"><span class="text-gray-400">-</span></div>`

          if (onSale) {
            return `
              <div class="flex items-center h-full">
                <span class="font-medium">$${price}</span>
                <span class="text-xs line-through text-gray-400 ml-1">$${compareAtPrice}</span>
              </div>
            `
          }

          return `<div class="flex items-center h-full"><span class="font-medium">$${price}</span></div>`
        }

      case "stockBadge":
        return (params) => {
          const inStock = params.data.in_stock
          const hasShopifyData = params.data.has_shopify_data

          if (!hasShopifyData) {
            return `<div class="flex items-center h-full"><span class="text-gray-400 text-sm">N/A</span></div>`
          }

          if (inStock) {
            return `<div class="flex items-center h-full"><span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">In Stock</span></div>`
          }

          return `<div class="flex items-center h-full"><span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400">Out of Stock</span></div>`
        }

      case "boostScore":
        return (params) => {
          const boostScore = params.value

          if (!boostScore || boostScore <= 1) {
            return `<div class="flex items-center h-full"><span class="text-gray-400">-</span></div>`
          }

          return `
            <div class="flex items-center h-full">
              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                ${boostScore}
              </span>
            </div>
          `
        }

      case "collectionWithImage":
        return (params) => {
          const url = params.data.show_url || `${baseUrl}/${params.data.id}`
          const imageUrl = params.data.image_url
          const handle = params.data.handle
          const productsCount = params.data.products_count

          const imageHtml = imageUrl
            ? `<img class="h-10 w-10 rounded-lg object-cover" src="${imageUrl}" alt="${params.value}">`
            : `<div class="h-10 w-10 bg-gray-200 dark:bg-neutral-600 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-gray-400 dark:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>`

          const subtitleParts = []
          if (handle) subtitleParts.push(`<code>${handle}</code>`)
          if (productsCount !== undefined) subtitleParts.push(`${productsCount} products`)
          const subtitle = subtitleParts.join(" · ")

          return `
            <div class="flex items-center h-full">
              <a href="${url}" data-turbo-frame="_top" class="flex-shrink-0">${imageHtml}</a>
              <div class="ml-4 min-w-0">
                <a href="${url}" data-turbo-frame="_top" class="text-sm font-medium text-gray-900 dark:text-neutral-200 hover:text-blue-600 dark:hover:text-blue-400 block truncate">${params.value}</a>
                ${subtitle ? `<div class="text-xs text-gray-500 dark:text-neutral-400 mt-0.5">${subtitle}</div>` : ""}
              </div>
            </div>
          `
        }

      case "location":
        return (params) => {
          const flag = params.data.country_flag
          const location = params.value || params.data.location

          if (!flag && !location) {
            return `<div class="flex items-center h-full"><span class="text-gray-400">-</span></div>`
          }

          return `<div class="flex items-center h-full gap-2"><span class="text-lg">${flag || ""}</span><span class="text-sm text-gray-700 dark:text-gray-300">${location || ""}</span></div>`
        }

      case "messageCount":
        return (params) => {
          const count = params.value || 0
          return `<div class="flex items-center h-full"><span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-neutral-700 dark:text-gray-300">${count} messages</span></div>`
        }

      case "dateTime":
        return (params) => {
          const value = params.value
          if (!value || value === "-") return `<div class="flex items-center h-full"><span class="text-gray-400">-</span></div>`

          // Parse ISO date and format for display
          const date = new Date(value)
          if (isNaN(date.getTime())) {
            // If not a valid date, just show the value as-is
            return `<div class="flex items-center h-full"><span class="text-sm text-gray-700 dark:text-gray-300">${value}</span></div>`
          }

          const formatted = date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
          })
          return `<div class="flex items-center h-full"><span class="text-sm text-gray-700 dark:text-gray-300">${formatted}</span></div>`
        }

      case "ipAddress":
        return (params) => {
          const url = params.data.show_url || `${baseUrl}/${params.data.id}`
          const ip = params.value
          const displayText = ip || `#${params.data.id}`
          return `<a href="${url}" data-turbo-frame="_top" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline font-mono text-sm">${displayText}</a>`
        }

      case "customer":
        return (params) => {
          const email = params.data.customer_email
          const name = params.data.customer_name

          if (!email && !name) {
            return `<div class="flex items-center h-full"><span class="text-gray-400 text-sm">-</span></div>`
          }

          const displayName = name || email
          const subtitle = name && email ? email : null

          return `
            <div class="flex items-center h-full">
              <div class="min-w-0">
                <div class="text-sm font-medium text-gray-900 dark:text-neutral-200 truncate">${displayName}</div>
                ${subtitle ? `<div class="text-xs text-gray-500 dark:text-neutral-400 truncate">${subtitle}</div>` : ""}
              </div>
            </div>
          `
        }

      case "email":
        return (params) => {
          const email = params.value
          if (!email || email === "-") {
            return `<div class="flex items-center h-full"><span class="text-gray-400 text-sm">-</span></div>`
          }
          const url = params.data.show_url || `${baseUrl}/${params.data.id}`
          return `<div class="flex items-center h-full"><a href="${url}" data-turbo-frame="_top" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline text-sm truncate">${email}</a></div>`
        }

      case "ordersCount":
        return (params) => {
          const count = params.value || 0
          if (count === 0) {
            return `<div class="flex items-center h-full"><span class="text-gray-400 text-sm">-</span></div>`
          }
          return `<div class="flex items-center h-full"><span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">${count} orders</span></div>`
        }

      case "customerBadges":
        return (params) => {
          const badges = []
          if (params.data.vip) {
            badges.push(`<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">VIP</span>`)
          }
          if (params.data.known_good) {
            badges.push(`<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Good</span>`)
          }
          if (params.data.known_bad) {
            badges.push(`<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Bad</span>`)
          }
          if (params.data.is_reseller) {
            badges.push(`<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">Reseller</span>`)
          }

          if (badges.length === 0) {
            return `<div class="flex items-center h-full"><span class="text-gray-400 text-sm">-</span></div>`
          }

          return `<div class="flex items-center h-full gap-1 flex-wrap">${badges.join("")}</div>`
        }

      case "number":
        return (params) => {
          const value = params.value
          if (value == null || value === 0) {
            return `<div class="flex items-center h-full"><span class="text-gray-400 text-sm">-</span></div>`
          }
          return `<div class="flex items-center h-full"><span class="text-sm text-gray-700 dark:text-gray-300">${value}</span></div>`
        }

      default:
        return null
    }
  }

  getBadgeColorClasses(color) {
    const colors = {
      blue: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
      purple: "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
      green: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
      red: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
      yellow: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
      gray: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
      indigo: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400",
      pink: "bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-400"
    }
    return colors[color] || colors.gray
  }

  getIconBgClass(color) {
    const colors = {
      blue: "bg-blue-100 dark:bg-blue-900/20",
      purple: "bg-purple-100 dark:bg-purple-900/20",
      green: "bg-green-100 dark:bg-green-900/20",
      red: "bg-red-100 dark:bg-red-900/20",
      yellow: "bg-yellow-100 dark:bg-yellow-900/20",
      gray: "bg-gray-100 dark:bg-gray-700",
      indigo: "bg-indigo-100 dark:bg-indigo-900/20",
      pink: "bg-pink-100 dark:bg-pink-900/20"
    }
    return colors[color] || colors.gray
  }

  getIconColorClass(color) {
    const colors = {
      blue: "text-blue-600 dark:text-blue-400",
      purple: "text-purple-600 dark:text-purple-400",
      green: "text-green-600 dark:text-green-400",
      red: "text-red-600 dark:text-red-400",
      yellow: "text-yellow-600 dark:text-yellow-400",
      gray: "text-gray-600 dark:text-gray-400",
      indigo: "text-indigo-600 dark:text-indigo-400",
      pink: "text-pink-600 dark:text-pink-400"
    }
    return colors[color] || colors.gray
  }

  getIcon(name, color) {
    const colorClass = this.getIconColorClass(color)
    const icons = {
      document: `<svg class="w-4 h-4 ${colorClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>`,
      question: `<svg class="w-4 h-4 ${colorClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`,
      box: `<svg class="w-4 h-4 ${colorClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>`,
      check: `<svg class="w-4 h-4 ${colorClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>`,
      x: `<svg class="w-4 h-4 ${colorClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>`
    }
    return icons[name] || icons.document
  }

  async loadData() {
    if (this.hasRowDataValue && this.rowDataValue.length > 0) {
      // Data already provided inline
      return
    }

    if (!this.hasDataUrlValue) return

    try {
      const response = await fetch(this.dataUrlValue, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.totalRowCount = data.length
        this.gridApi.setGridOption("rowData", data)
        this.updateCount()
      } else {
        console.error("Failed to load data:", response.statusText)
      }
    } catch (error) {
      console.error("Error loading data:", error)
    }
  }

  onGridReady(params) {
    this.gridApi = params.api
  }

  onRowDoubleClicked(event) {
    const url = event.data.show_url || (this.hasBaseUrlValue ? `${this.baseUrlValue}/${event.data.id}` : null)
    if (url) {
      window.location.href = url
    }
  }

  onRowDataUpdated() {
    this.updateCount()
  }

  updateCount() {
    if (!this.hasCountTarget) return

    const displayedCount = this.gridApi ? this.gridApi.getDisplayedRowCount() : 0
    const totalCount = this.totalRowCount || displayedCount

    if (this.currentFilter === "all") {
      this.countTarget.textContent = `${totalCount} items`
    } else {
      this.countTarget.textContent = `${displayedCount} of ${totalCount} items`
    }
  }

  // External filter for tabs
  doesExternalFilterPass(node) {
    if (this.currentFilter === "all") return true
    if (!this.hasFilterFieldValue) return true

    const fieldValue = node.data[this.filterFieldValue]
    return String(fieldValue).toLowerCase() === String(this.currentFilter).toLowerCase()
  }

  // Tab click handler - Stimulus action
  filterByType(event) {
    const filterType = event.currentTarget.dataset.filterType || event.params.type
    this.currentFilter = filterType

    // Update tab active states
    this.tabTargets.forEach(tab => {
      const tabType = tab.dataset.filterType || tab.dataset.aggridTypeParam
      const isActive = tabType === filterType

      if (isActive) {
        tab.classList.remove("ag-grid-tabs-inactive")
        tab.classList.add("ag-grid-tabs-active")
      } else {
        tab.classList.remove("ag-grid-tabs-active")
        tab.classList.add("ag-grid-tabs-inactive")
      }
    })

    // Trigger external filter
    if (this.gridApi) {
      this.gridApi.onFilterChanged()
      this.updateCount()
    }
  }

  // Search input handler - Stimulus action
  search(event) {
    const value = event.target.value

    if (this.serverSideSearchValue) {
      // Server-side search with debouncing
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        this.performServerSearch(value)
      }, this.searchDebounceValue)
    } else {
      // Local quick filter search
      if (this.gridApi) {
        this.gridApi.setGridOption("quickFilterText", value)
        this.updateCount()
      }
    }
  }

  async performServerSearch(query) {
    if (!this.hasDataUrlValue) return

    try {
      // Build URL with search query
      const url = new URL(this.dataUrlValue, window.location.origin)
      if (query && query.trim() !== "") {
        url.searchParams.set("q", query.trim())
      }

      const response = await fetch(url.toString(), {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.totalRowCount = data.length
        this.gridApi.setGridOption("rowData", data)
        this.updateCount()
      } else {
        console.error("Search failed:", response.statusText)
      }
    } catch (error) {
      console.error("Search error:", error)
    }
  }

  // Checkbox toggle handler
  async toggleCheckbox(event) {
    if (!this.hasToggleUrlValue) return

    const checkbox = event.target
    const id = checkbox.dataset.id
    const checked = checkbox.checked

    try {
      const url = this.toggleUrlValue.replace(":id", id)
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ enabled: checked })
      })

      if (!response.ok) {
        // Revert checkbox on error
        checkbox.checked = !checked
        console.error("Failed to update:", response.statusText)
      }
    } catch (error) {
      checkbox.checked = !checked
      console.error("Error updating:", error)
    }
  }

  // Refresh data - Stimulus action
  refresh() {
    this.loadData()
  }

  // Export to CSV - Stimulus action
  exportCsv() {
    if (this.gridApi) {
      this.gridApi.exportDataAsCsv()
    }
  }

  // Export to Excel - Stimulus action
  exportExcel() {
    if (this.gridApi) {
      this.gridApi.exportDataAsExcel()
    }
  }

  // Dynamic height helpers
  calculateDynamicHeight() {
    const viewportHeight = window.innerHeight
    const gridTop = this.gridTarget.getBoundingClientRect().top
    const offset = this.heightOffsetValue
    const availableHeight = viewportHeight - gridTop - offset
    return Math.max(availableHeight, 300) + "px"
  }

  handleResize() {
    if (this.dynamicHeightValue) {
      this.gridTarget.style.height = this.calculateDynamicHeight()
      if (this.gridApi) {
        setTimeout(() => this.gridApi.sizeColumnsToFit(), 100)
      }
    }
  }
}
